import { ArrowUpRight, MapPin } from "lucide-react";
import { FEATURED_WORK } from "#/lib/constants";
import { img } from "#/lib/images";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";

/**
 * /our-work — clickable recent-work photo grid. Each card deep-links to its
 * service's detail block on /services (finish photos, overview, and the
 * "how we do it best" process for that kind of work).
 */
export function RecentWorkGrid() {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <section className="bg-light-bg py-14 lg:py-20">
      <div ref={ref} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionLabel>Recent Work</SectionLabel>
        <h2
          className={`mt-4 max-w-2xl font-heading text-3xl font-extrabold leading-[1.1] text-graphite transition-all duration-700 sm:text-4xl ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          Fresh off the forms.
        </h2>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-concrete-dark sm:text-base">
          Tap any project to see what that kind of work looks like finished and
          how we build it, step by step.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURED_WORK.map((work, i) => (
            <a
              key={work.title}
              href={`/services#${work.service}`}
              className={`group overflow-hidden rounded-lg bg-white no-underline ring-1 ring-gray-200 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:ring-flame/40 ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: isVisible ? `${i * 70}ms` : "0ms" }}
            >
              <div className="overflow-hidden">
                <img
                  src={img(work.img, { w: 640, h: 480 })}
                  alt={`${work.title} in ${work.location}`}
                  width={640}
                  height={480}
                  loading="lazy"
                  decoding="async"
                  className="aspect-4/3 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-flame">
                    {work.tag}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-concrete-dark">
                    <MapPin className="h-3.5 w-3.5" />
                    {work.location}
                  </span>
                </div>
                <h3 className="mt-2 font-heading text-lg font-bold text-graphite">
                  {work.title}
                </h3>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-flame">
                  More information
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
